import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  checkFactoringRequestDeletion,
  deleteFactoringRequest,
} from '../../../../../../../../../../../../../../../lib/api/factoring';
import CustomButton from '../../../../../../../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../../../../../../../lib/designSystem/CircularProgress';
import DialogTitle from '../../../../../../../../../../../../../../../lib/designSystem/DialogTitle';

interface FactoringRequestDeleteConfirmationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requestId: number;
  setUp: () => void;
}

const FactoringRequestDeleteConfirmationModal = (
  props: FactoringRequestDeleteConfirmationModalProps,
) => {
  const { open, setOpen, requestId, setUp } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [reason, setReason] = useState('');

  const handleDeleteFactoringRequest = async () => {
    setLoading(true);
    try {
      await deleteFactoringRequest(dispatch, requestId);
      setUp();
      showAlert(
        dispatch,
        'success',
        'La solicitud de factoring ha sido eliminada exitosamente.',
      );
      setOpen(false);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar eliminar la solicitud de factoring.',
      );
    }
    setLoading(false);
  };

  const checkIfCanDelete = async () => {
    setLoading(true);
    try {
      const response = await checkFactoringRequestDeletion(dispatch, requestId);
      setCanDelete(response.canDelete);
      setReason(response.reason ?? '');
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar verificar si la solicitud de factoring puede ser eliminada.',
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    void checkIfCanDelete();
  }, []);

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogTitle setOpen={setOpen}>
        Eliminar solicitud #{requestId}
      </DialogTitle>
      {loading ? (
        <DialogContent style={{ minHeight: 200 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <div>
          <DialogContent>
            <Typography>
              Al borrar una solicitud se borrarán todas sus dependencias:
            </Typography>
            <div style={{ padding: 8 }}>
              {[
                'Las aprobaciones de esta solicitud',
                'Pagos realizados debido a esta solicitud',
                'Conciliaciones hechas al activar esta solicitud',
                'Montos en garantía de esta solicitud',
                'CxP creadas a partir de la activación de esta solicitud',
              ].map((text, index) => (
                <Typography key={index} variant="body1">
                  - {text}
                </Typography>
              ))}
            </div>
            {reason && (
              <div style={{ padding: '8px 0px 0px' }}>
                <Typography variant="body1" color="error">
                  La solicitud no se puede eliminar porque:
                </Typography>
                <Typography variant="body1" color="error">
                  - {reason}
                </Typography>
              </div>
            )}
            <div style={{ padding: '8px 0px 0px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setAgreeTerms(!agreeTerms)}
                    value={agreeTerms}
                    checked={agreeTerms}
                  />
                }
                label={
                  <Typography variant="body2">
                    Estoy consciente de que al eliminar esta solicitud no podré
                    recuperarla
                  </Typography>
                }
              />
            </div>
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpen(false)}
              variant="outlined"
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={() => handleDeleteFactoringRequest()}
              variant="contained"
              color="error"
              disabled={!agreeTerms || !canDelete}
            >
              Eliminar
            </CustomButton>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default FactoringRequestDeleteConfirmationModal;
